import { useState, useEffect, useCallback } from "react";
import Select, { MultiValue, StylesConfig, components } from "react-select";
import { Option } from "../../interface/Option";
import { CustomIcdAxiosInstance } from "../../Network/CustomIcd";

interface ListofCustomIcdProps {
  onChange: (selected: any) => void;
  clear?: boolean;
  defaultValue?: any;
  qaIdPrefix?: string;
  [key: string]: any;
}

const DEFAULT_QA_PREFIX = "list-of-custom-icd";

const qaSafe = (value: string | number | undefined | null) =>
  String(value ?? "unknown")
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");

const buildQaId = (prefix: string, suffix: string | number) =>
  `${qaSafe(prefix)}-${qaSafe(suffix)}`;

const qaAttrs = (prefix: string, suffix: string | number) => {
  const id = buildQaId(prefix, suffix);
  return {
    id,
    "data-testid": id,
    "data-qa": id,
  };
};

const ListofCustomIcd = ({
  onChange,
  clear,
  defaultValue,
  qaIdPrefix = DEFAULT_QA_PREFIX,
}: ListofCustomIcdProps) => {
  const [options, setOptions] = useState<Option[]>([]);
  const [selectedOptions, setSelectedOptions] = useState<Option[]>([]);
  const [loading, setLoading] = useState(false);
  const [inputValue, setInputValue] = useState<string>("");

  const selectQaId = buildQaId(qaIdPrefix, "select");
  const inputQaId = buildQaId(qaIdPrefix, "input");

  // loadOptions
  const loadOptions = useCallback(async () => {
    if (inputValue.trim().length < 3) {
      setOptions([]);
      return;
    }

    setLoading(true);
    try {
      const response = await CustomIcdAxiosInstance.get(`searchCustomGroup`, {
        params: { name: inputValue, active: true },
      });

      const data = response.data?.result || [];
      const formattedOptions: Option[] = data.map((item: any) => ({
        value: item.id.toString(),
        label: `(${item.id}) ${item.name}`,
      }));

      setOptions(formattedOptions);
    } catch (error) {
      console.error("Error loading options:", error);
    } finally {
      setLoading(false);
    }
  }, [inputValue]);

  // Call load when searching
  useEffect(() => {
    const debounceTimeout = setTimeout(() => {
      loadOptions();
    }, 300);

    return () => clearTimeout(debounceTimeout);
  }, [inputValue, loadOptions]);

  // Update the selected values
  const handleChange = (selected: MultiValue<Option>) => {
    setSelectedOptions(selected as Option[]);
    onChange(selected as Option[]);
  };

  // Reset selected values ​​when clearing values
  useEffect(() => {
    if (clear) {
      setSelectedOptions([]);
    }
  }, [clear]);

  // Set default values ​​on load
  useEffect(() => {
    if (defaultValue) {
      setSelectedOptions(defaultValue);
    }
  }, [defaultValue]);

  // styles
  const customStyles: StylesConfig<Option, true> = {
    multiValue: (styles) => ({
      ...styles,
      backgroundColor: "#007BFF",
      color: "#FFFFFF",
      borderRadius: "5px",
    }),
    multiValueLabel: (styles) => ({
      ...styles,
      color: "#FFFFFF",
    }),
    multiValueRemove: (styles) => ({
      ...styles,
      color: "#FFFFFF",
      ":hover": {
        backgroundColor: "#0056b3",
        color: "#FFFFFF",
      },
    }),
    option: (styles, { isSelected }) => ({
      ...styles,
      backgroundColor: isSelected ? "#007BFF" : "#FFFFFF",
      color: isSelected ? "#FFFFFF" : "#000000",
      ":hover": {
        backgroundColor: "#0056b3",
        color: "#FFFFFF",
      },
    }),
  };

  const Menu = (props: any) => (
    <div {...qaAttrs(qaIdPrefix, "menu")}>
      <components.Menu {...props} />
    </div>
  );

  const OptionComponent = (props: any) => {
    const optionId = qaSafe(String(props.data?.value ?? "unknown"));
    return (
      <div
        {...qaAttrs(qaIdPrefix, `option-${optionId}`)}
        data-option-label={props.data?.label ?? ""}
        data-option-value={props.data?.value ?? ""}
      >
        <components.Option {...props} />
      </div>
    );
  };

  const MultiValueComponent = (props: any) => {
    const selectedId = qaSafe(String(props.data?.value ?? "unknown"));
    return (
      <div
        {...qaAttrs(qaIdPrefix, `selected-${selectedId}`)}
        data-selected-label={props.data?.label ?? ""}
        data-selected-value={props.data?.value ?? ""}
      >
        <components.MultiValue {...props} />
      </div>
    );
  };

  return (
    <div {...qaAttrs(qaIdPrefix, "wrapper")}>
      <Select
        isMulti
        inputId={inputQaId}
        instanceId={selectQaId}
        name={selectQaId}
        classNamePrefix={selectQaId}
        className={selectQaId}
        aria-label="Custom ICD group selector"
        data-testid={selectQaId}
        data-qa={selectQaId}
        value={selectedOptions}
        onChange={handleChange}
        options={options}
        isLoading={loading}
        onInputChange={setInputValue}
        placeholder="Type at least 3 letters to search..."
        styles={customStyles} // Apply colors
        components={{
          Menu,
          Option: OptionComponent,
          MultiValue: MultiValueComponent,
        }}
      />

      <span
        {...qaAttrs(qaIdPrefix, "loading-state")}
        data-loading={loading ? "true" : "false"}
        style={{ display: "none" }}
      >
        {loading ? "Loading" : "Idle"}
      </span>

      <span
        {...qaAttrs(qaIdPrefix, "selected-count")}
        data-count={selectedOptions.length}
        style={{ display: "none" }}
      >
        {selectedOptions.length}
      </span>

      <span
        {...qaAttrs(qaIdPrefix, "options-count")}
        data-count={options.length}
        style={{ display: "none" }}
      >
        {options.length}
      </span>

      <span
        {...qaAttrs(qaIdPrefix, "input-value")}
        data-value={inputValue}
        style={{ display: "none" }}
      >
        {inputValue}
      </span>
    </div>
  );
};

export default ListofCustomIcd;
